import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBusiness } from '../context/BusinessContext';
import { listCalls } from '../services/api';
import { CallLog } from '../types';
import { format } from 'date-fns';

export default function CallsPage() {
  const { currentBusiness } = useBusiness();
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  const fetchCalls = async (startAfter?: string) => {
    if (!currentBusiness) return;

    setLoading(true);
    try {
      const data = await listCalls(currentBusiness.id, 25, startAfter);
      if (startAfter) {
        setCalls((prev) => [...prev, ...data]);
      } else {
        setCalls(data);
      }
      setHasMore(data.length === 25);
    } catch (error) {
      console.error('Error fetching calls:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, [currentBusiness]);

  const loadMore = () => {
    if (calls.length > 0) {
      fetchCalls(calls[calls.length - 1].id);
    }
  };

  if (!currentBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a business</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="page-header">Call Logs</h1>

      <div className="card">
        {loading && calls.length === 0 ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : calls.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No calls recorded yet</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="table-header">Caller</th>
                    <th className="table-header">Date & Time</th>
                    <th className="table-header">Duration</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Info Collected</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {calls.map((call) => (
                    <tr key={call.id} className="hover:bg-gray-50">
                      <td className="table-cell">
                        <div>
                          <p className="font-medium">{call.callerNumber}</p>
                          {call.extractedData?.callerName && (
                            <p className="text-gray-500 text-xs">
                              {call.extractedData.callerName}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        {call.createdAt?.toDate()
                          ? format(call.createdAt.toDate(), 'MMM d, yyyy h:mm a')
                          : 'Unknown'}
                      </td>
                      <td className="table-cell">
                        {call.duration
                          ? `${Math.floor(call.duration / 60)}:${String(
                              call.duration % 60
                            ).padStart(2, '0')}`
                          : '-'}
                      </td>
                      <td className="table-cell">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            call.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : call.status === 'in-progress'
                              ? 'bg-blue-100 text-blue-800'
                              : call.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {call.status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex gap-1 flex-wrap">
                          {call.extractedData?.callerName && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                              Name
                            </span>
                          )}
                          {call.extractedData?.callerEmail && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700">
                              Email
                            </span>
                          )}
                          {call.extractedData?.appointmentDate && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700">
                              Appt
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <Link
                          to={`/calls/${call.id}`}
                          className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div className="mt-4 text-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="btn-secondary"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
